/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/**
* Wait for an element before resolving a promise
* @param {String} querySelector - Selector of element to wait for
* @param {Integer} timeout - Milliseconds to wait before timing out, or 0 for no timeout
*/
theme.waitForElement = function (querySelector, timeout = 0) {
  return new Promise((resolve, reject)=>{
    var timer = false;
    if(document.querySelectorAll(querySelector).length) return resolve();
    const observer = new MutationObserver(()=>{
      if(document.querySelectorAll(querySelector).length){
        observer.disconnect();
        if(timer !== false) clearTimeout(timer);
        return resolve();
      }
    });
    observer.observe(document.body, {
      childList: true, 
      subtree: true
    });
    if(timeout) timer = setTimeout(()=>{
      observer.disconnect();
      reject();
    }, timeout);
  });
 };
 
/**
 * Wait for an element to disappear before resolving a promise
 * @param {String} querySelector - Selector of element to wait for
 * @param {Integer} timeout - Milliseconds to wait before timing out, or 0 for no timeout
 */
theme.waitForElementDisappear = function (querySelector, timeout = 0) {
  return new Promise((resolve, reject)=>{
    var timer = false;
    if(document.querySelectorAll(querySelector).length == 0) return resolve();
    const observer = new MutationObserver(()=>{
      if(document.querySelectorAll(querySelector).length == 0){
        observer.disconnect();
        if(timer !== false) clearTimeout(timer);
        return resolve();
      }
    });
    observer.observe(document.body, {
      childList: true, 
      subtree: true
    });
    if(timeout) timer = setTimeout(()=>{
      observer.disconnect();
      reject();
    }, timeout);
  });
};


// Style the product title to show the subtitle as second line
theme.displayProductSubtitle = function (title) {
  // Extract text nodes from the title element
  var textNodes = [];
  title.childNodes.forEach(function(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      textNodes.push(node);
    }
  });

  // Combine the text content of all text nodes
  var combinedText = textNodes.map(function(node) {
    return node.nodeValue;
  }).join('').trim();

  // Match pattern: UPPERCASE LETTERS title followed by subtitle (including numbers and foreign languages)
  // Example: "FOREVER YOUNG 抗老化美容液" → title: "FOREVER YOUNG", subtitle: "抗老化美容液"
  // Example: "ROUTINE EN 4 ÉTAPES Coffret Personnalisé" → title: "ROUTINE EN", subtitle: "4 ÉTAPES Coffret Personnalisé"
  const match = combinedText.match(/^([A-Z]['A-Z\s-]*)\s+(.+)$/);

  if (match) {
    const subtitle = match[2].trim();

    // Replace the subtitle in the text nodes
    textNodes.forEach(function(node) {
      if (node.nodeValue.includes(subtitle)) {
        node.nodeValue = node.nodeValue.replace(subtitle, '');
        var span = document.createElement('span');
        span.className = 'product-subtitle';
        span.textContent = subtitle;
        node.parentNode.insertBefore(span, node.nextSibling);
      }
    });
  }
};

theme.convertCurrencyToDouble = function (currency, locale, currencyType) {

  // If locale is empty, use Shopify.locale variable
  if (!locale) {
    locale = `${Shopify.locale}-${Shopify.country}`;
  }

  if (!currencyType) {
    currencyType = Shopify.currency.active;
  }

  // Creating a NumberFormat object with specified locale and currency
  const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: currencyType });

  // Formatting the currency to remove any non-numeric characters except for decimal point
  const normalizedCurrency = currency.replace(/[^\d.-]+/g, "");

  // Converting the normalized string to a double value using Number constructor
  const doubleValue = Number(normalizedCurrency);

  // Returning the double value
  return doubleValue;
};

theme.hashUrlHandler = function () {
  const url = window.location;
  const hash = url.hash;

  const clearHash = () => {
    history.replaceState("", document.title, url.origin + url.pathname + url.search);
  };

  const handleHash = (hashString, elementSelector, callback) => {
    if (hash.indexOf(hashString) !== -1) {
      theme.waitForElement(elementSelector, 3000).then(callback);
      clearHash();
    }
  };

  // Handle #header-btn
  handleHash('#header-btn', 'a.header-btn', () => {
    document.querySelector('a.header-btn').click();
  });

  // Handle #SnWPopup
  handleHash('#SnWPopup', '#crazyrocket-bdn-window', () => {
    CrazyRocket.showPopUp({ forceShow: true });
  });

  // Handle #IRENSkipPlastics
  handleHash('#IRENSkipPlastics', '.rhym-float-widget', () => {
    onOpenModal('GAM910213932581');
  });

};

theme.localeUrlHandler = function () {
  // Retrieve the Shopify root URL; exit early if not defined or if it's just "/"
  const shopifyRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  if (!shopifyRoot || shopifyRoot === "/") return;
  const shopifyRootNoSlash = shopifyRoot.slice(0, -1);

  // Process each anchor element with an href attribute
  document.querySelectorAll('a[href]').forEach(anchor => {
    const href = anchor.getAttribute('href');
    // Only modify hrefs that start with a single "/" (excluding URLs starting with "//")
    if (!href || href[0] !== '/' || href.startsWith('//')) return;
    // Skip if the href exactly matches shopifyRoot without the trailing slash or already starts with shopifyRoot (e.g. /zh-cn/ or /zh-cn)
    if (href === shopifyRootNoSlash || href.startsWith(shopifyRoot)) return;
    // Update the href by removing the leading "/" and prepending shopifyRoot
    anchor.setAttribute('href', shopifyRoot + href.slice(1));
  });  
};

/**
 * Email verification for newsletter signup forms
 * @param {String} formId - ID of the form to verify
 */
theme.verifyEmail = function(formId) {
  const API_KEY = 'YIDzBbDmNeqI3is0v0vyAsa3ePiHJ5dN';
  
  const form = document.getElementById(formId);
  if (!form) return;
  
  const emailInput = form.querySelector('input[type="email"]');
  const submitButton = form.querySelector('button[type="submit"]');
  
  if (!emailInput || !submitButton) return;
  
  // Store original button content
  const originalButtonContent = submitButton.innerHTML;
  
  // Clear custom validity on input
  emailInput.addEventListener('input', function() {
    emailInput.setCustomValidity('');
  });
  
  submitButton.addEventListener('click', async function(e) {
    e.preventDefault();
    
    const email = emailInput.value.trim();
    
    if (!email) {
      return;
    }
    
    // Clear any previous custom validity
    emailInput.setCustomValidity('');
    
    // Set fixed dimensions and show loading state
    submitButton.style.height = submitButton.offsetHeight + 'px';
    submitButton.style.width = submitButton.offsetWidth + 'px';
    submitButton.disabled = true;
    submitButton.classList.add('loading');
    submitButton.innerHTML = '<div class="loading-spinner"></div>';
    
    try {
      const response = await fetch(`https://emailverifier.reoon.com/api/v1/verify?email=${encodeURIComponent(email)}&key=${API_KEY}&mode=quick`);
      const data = await response.json();
      
      if (data.status === 'valid') {
        // Email is valid, submit the form
        form.submit();
      } else {
        // Email is invalid - set custom validity message
        let message = theme.strings.e;
        
        if (data.status === 'disposable') {
          message = theme.strings.disposableEmail;
        } else if (data.status === 'spamtrap') {
          message = theme.strings.spamEmail;
        } else if (data.status === 'invalid') {
          message = theme.strings.invalidEmail;
        }
        
        emailInput.setCustomValidity(message);
        emailInput.reportValidity();
        
        // Reset button state
        submitButton.disabled = false;
        submitButton.classList.remove('loading');
        submitButton.innerHTML = originalButtonContent;
        submitButton.style.height = '';
        submitButton.style.width = '';
      }
    } catch (error) {
      console.error('Email verification error:', error);
      // On error, allow form submission anyway
      form.submit();
    }
  });
};

window.addEventListener('DOMContentLoaded', theme.hashUrlHandler);
window.addEventListener('hashchange', theme.hashUrlHandler);

document.addEventListener('DOMContentLoaded', function() {

  // Convert all relative URLs to locale-aware URLs
  theme.localeUrlHandler();

  // Disable auto-redirect by Geolocation Redirects app when testing
  if (window.location.hash === '#ngr-test') {
    window.sessionStorage.setItem('ngr-auto', '1');
    history.replaceState("", document.title, window.location.origin + window.location.pathname + window.location.search);
  }

  // Redirect to the relevant market based on the URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const market = urlParams.get('market');
  if (market) {
    theme.waitForElement('.localization__selector', 3000).then(function() {
      const input = document.querySelector('.localization__selector input[name="country_code"]');
      const form = input.closest('form');
      // Check if the localization selector value has 'changed' and the market is available - prevent endless loop redirect
      if (input && form && input.value != market && theme.settings.availableCountriesInMarket.includes(market)) {
        input.value = market;
        form.submit();
      }
    });
  }

  // Stop the scrolling of the page when the CrazyRocket popup is opened
  const crazyRocketObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
        const overlay = mutation.target;
        const displayStyle = window.getComputedStyle(overlay).display;
        
        if (displayStyle === 'block') {
          document.body.classList.add('overflow-hidden');
          document.documentElement.style.scrollBehavior = 'auto';
        } else if (displayStyle === 'none') {
          document.body.classList.remove('overflow-hidden');
          document.documentElement.style.scrollBehavior = 'smooth';
        }
      }
    });
  });

  // Wait for the CrazyRocket overlay element to exist, then observe it
  theme.waitForElement('#crazyrocket-popup-overlay', 5000).then(() => {
    const overlay = document.getElementById('crazyrocket-popup-overlay');
    if (overlay) {
      crazyRocketObserver.observe(overlay, { attributes: true, attributeFilter: ['style'] });
    }
  }).catch(() => {
    // console.log('CrazyRocket popup overlay not found');
  });

/* =================================================================================================
 * Growave Addon
 * =================================================================================================
*/
  const growaveHandlers = {};
  const gwReview = document.getElementsByClassName('gw-rv-main-widget-placeholder');
  const gwReviewsPage = document.getElementsByClassName('gw-rv-reviews-page-widget-placeholder');
  const gwLoyaltyInfo = document.getElementsByClassName('gw-rd-loyalty-info-page-widget-placeholder');
  const gwWaystoEarn = document.getElementsByClassName('gw-rd-ways-to-earn-page-widget-placeholder');
  const gwWaystoSpend = document.getElementsByClassName('gw-rd-ways-to-spend-page-widget-placeholder');
  const gwInstagramGallery = document.getElementsByClassName('gw-instagram-gallery-placeholder');
  const gwInstagramSlider = document.getElementById('gw-instagram-widget-slider');

  // Make the instagram widget into a slider
  growaveHandlers.instagramSlider = () => {
    theme.waitForElement('.gw-ig-gallery-widget').then(function() {
      theme.waitForElement('.gw-ig-gallery-widget .gw-ig-post-card .gw-image', 3000).then(function() {
        
        const imageObserver = new MutationObserver(() => {

          setTimeout(() => {
            const slider = gwInstagramSlider.querySelector('.gw-ig-gallery-widget');

            if (slider.childElementCount > 0) {
              gwInstagramSlider.querySelector('.gw-ig-gallery-widget').classList.add('slider','slider--no-scrollbar');
              gwInstagramSlider.querySelector('.gw-ig-gallery-listing-layout').classList.add('slider__grid');
              const items = gwInstagramSlider.querySelectorAll('.gw-ig-gallery-listing-layout__post-wrapper');
              
              items.forEach((el) => {
                el.classList.add('slider__item');
              });

              const nav = gwInstagramSlider.querySelector('.slider-nav');
              const nextBtn = nav.querySelector('button[name="next"]');
              const prevBtn = nav.querySelector('button[name="prev"]');

              if (items.length > 1) {
                let slideSpan = items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left;
                let scrollPos = 0;
    
                prevBtn.disabled = true;
                nav.classList.remove('hidden');

                nav.addEventListener('click', (evt) => {
                  if ((evt.target.name === 'next')) {
                    scrollPos = slider.scrollLeft + slideSpan;
                  } else {
                    scrollPos = slider.scrollLeft - slideSpan;
                  }
              
                  slider.scrollTo({ left: scrollPos, behavior: 'smooth' });
                });

                slider.addEventListener('scroll', () => {
                  prevBtn.disabled = slider.scrollLeft === 0;
                  nextBtn.disabled = slider.scrollLeft >= (items.length - 1.2) * slideSpan;
                });

              }
              
              gwInstagramSlider.setAttribute('loaded', '');
            }
          }, 200);

          imageObserver.disconnect();

        });

        imageObserver.observe(gwInstagramSlider.querySelector('#gw-instagram-widget-slider .gw-ig-post-card .gw-image'), { attributes: true });
        
      }).catch(e => {
        // no instagram photos found
        // check if there is any faq index
        if (gwInstagramSlider.closest('.faq-index__item-container--no-index')) {
          gwInstagramSlider.closest('.section-faq-header').classList.remove('section-faq-header--with-index');
          gwInstagramSlider.closest('.faq-index').style.display = 'none';
        }
        console.log('Element(s) with ".gw-ig-post-card .gw-image" identifiers not found!');
      });
    });
  }

  if (gwInstagramSlider) {
    growaveHandlers.instagramSlider();
  }
  
  growaveHandlers.setStyleSheet = (root) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = document.querySelector('link[href*="growave.css"]').href;
    root.shadowRoot.appendChild(link);
  };

  if (gwReview.length > 0) {
    setTimeout(() => {
      growaveHandlers.setStyleSheet(gwReview[0]);
    }, 2000); // waiting for the shadowroot to appear
  }

  if (gwReviewsPage.length > 0) {
    setTimeout(() => {
      growaveHandlers.setStyleSheet(gwReviewsPage[0]);
      gwReviewsPage[0].setAttribute('loaded', '');
    }, 2000); // waiting for the shadowroot to appear
  }

  if (gwLoyaltyInfo.length > 0) {
    setTimeout(() => {
      growaveHandlers.setStyleSheet(gwLoyaltyInfo[0]);
    }, 2000); // waiting for the shadowroot to appear
  }

  if (gwWaystoEarn.length > 0) {
    setTimeout(() => {
      growaveHandlers.setStyleSheet(gwWaystoEarn[0]);
    }, 2000); // waiting for the shadowroot to appear
  }

  if (gwWaystoSpend.length > 0) {
    setTimeout(() => {
      growaveHandlers.setStyleSheet(gwWaystoSpend[0]);
    }, 2000); // waiting for the shadowroot to appear
  }

  growaveHandlers.instagramGallery = () => {

    const modalObserver = new MutationObserver(() => {

      const postModal = document.querySelector('.gw-ig-post-modal');

      // Format product titles
      const titles = postModal.querySelectorAll('.gw-post-modal-tag-card__title-text');
      titles.forEach((title) => {
        theme.displayProductSubtitle(title);
      });

      // Hide compare price if it is less than or equal to the actual price
      const comparePrices = postModal.querySelectorAll('.gw-post-modal-tag_card__compare-price');
      comparePrices.forEach((comparePrice) => {
        const comparePriceValue = theme.convertCurrencyToDouble(comparePrice.textContent);
        const priceValue = theme.convertCurrencyToDouble(comparePrice.parentNode.querySelector('.gw-post-modal-tag_card__price').textContent);
        if (comparePriceValue <= priceValue) {
          comparePrice.style.display = 'none';
        }
      });
      
      // Make all links in instagram widget to open in current tab instead of a new tab
      postModal.addEventListener('click', (evt) => {
        const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
        // If the href points to the same website, open in the current tab
        if (link && link.href && link.tagName === 'A' && window.location.hostname === new URL(link.href).hostname) {
          link.target = '_self';
        }
      });

      // Show/hide the thumbnail images
      const product_link = postModal.querySelector('.gw-post-modal-product-info__link');
      if(product_link) {
        const observer = new MutationObserver(()=>{

          // Format product title
          const product_title = postModal.querySelector('.gw-post-modal-product-info__title-text');
          theme.displayProductSubtitle(product_title);
          product_title.querySelector('.product-subtitle:nth-child(2)')?.remove(); // remove duplicate subtitle

          // Hide compare price if it is less than or equal to the actual price
          const comparePrices = postModal.querySelectorAll('.gw-post-modal-product-info__compare-price');
          comparePrices.forEach((comparePrice) => {
            const comparePriceValue = theme.convertCurrencyToDouble(comparePrice.textContent);
            const priceValue = theme.convertCurrencyToDouble(comparePrice.parentNode.querySelector('.gw-post-modal-product-info__price').textContent);
            if (comparePriceValue <= priceValue) {
              comparePrice.style.display = 'none';
            } else {
              comparePrice.style.display = 'block';
            }
          });

          const product_url = product_link.getAttribute('href');
    
          fetch(product_url).
          then((response) => {
            if (!response.ok) {
              throw new Error("HTTP error! Status: ".concat(response.status));
            }
            return response.text();
          }).
          then((html) => {
    
            const thumbImages = [...postModal.getElementsByClassName('gw-post-modal-product-image__dot')];  
            const doc = new DOMParser().parseFromString(html, "text/html");
            const variantMediaEl = doc.querySelector('.js-data-variant-media');

            // console.log(variantMediaEl);

            // Media grouping settings is not enabled - display all image thumbnails
            if (!variantMediaEl) {
              thumbImages.forEach((image) => { image.style.display = "block"; });
            }

            // Media grouping settings is enabled - display only image thumbnails on selected variant
            else {

              const dataVariantMedia = JSON.parse(variantMediaEl.textContent);

              // console.log(dataVariantMedia);

              const selectedVariant = postModal.querySelector('.gw-post-modal-product-info__variants input[type="radio"]:checked')?.value;

              // console.log(selectedVariant);

              Object.keys(dataVariantMedia.media).forEach((key) => {
                const mediaItem = dataVariantMedia.media[key];
                if (!selectedVariant || // there is no variant selected
                    mediaItem.group === selectedVariant || mediaItem.group === true ) {
                  thumbImages[mediaItem.index - 1].style.display = "block";
                } else {
                  thumbImages[mediaItem.index - 1].style.display = "none";
                }
              });              
            }
          });
        });

        observer.observe(product_link, { attributes: true });
        product_link.setAttribute('loaded', ''); // force trigger the observer
      }
      
    });
    
    gwInstagramGallery[0].addEventListener('click', (evt) => {
      theme.waitForElement('.gw-ig-post-modal .gw-modal-body', 0).then(() => {
        const modalBody = document.querySelector('.gw-ig-post-modal .gw-modal-body');
        modalObserver.observe(modalBody, { attributes: true, childList: true });
        modalBody.setAttribute('loaded', ''); // force trigger the observer
      });
    });
 
  };

  if (gwInstagramGallery.length > 0) {
    growaveHandlers.instagramGallery();
  }

  // Accordion table for showing the delivery times and rates on shipping policy page
  if (expandoTable = document.querySelector('.expando-table')) {

    // Select all headers that are not expanded and hide their next siblings
    const headers = expandoTable.querySelectorAll('.header');
    const nonExpandedHeaders = Array.from(headers).filter(header => !header.classList.contains('expand'));
    nonExpandedHeaders.forEach(header => header.nextElementSibling.style.display = 'none');

    // Set up a click event listener on all headers
    expandoTable.addEventListener('click', function(event) {
        const target = event.target.closest('.header');
        if (target) {
            // Process only headers
            headers.forEach(header => {
                if (header !== target && header.classList.contains('expand')) {
                    header.classList.remove('expand');
                    slideToggle(header.nextElementSibling, 500);
                }
            });

            target.classList.toggle('expand');
            slideToggle(target.nextElementSibling, 500);
        }
    });


    // Helper function to replicate jQuery's slideToggle effect
    function slideToggle(element, duration) {
      if (element.style.display === 'none') {
          slideDown(element, duration);
      } else {
          slideUp(element, duration);
      }
    }
    
    // Helper function to slide down an element
    function slideDown(element, duration) {
        element.style.removeProperty('display');
        let display = window.getComputedStyle(element).display;
        if (display === 'none') display = 'block';
        element.style.display = display;
        let height = element.scrollHeight;
    
        element.style.overflow = 'hidden';
        element.style.height = 0;
        element.style.paddingTop = 0;
        element.style.paddingBottom = 0;
        element.style.transitionProperty = `height, margin, padding`;
        element.style.transitionDuration = duration + 'ms';
        element.offsetHeight; // force repaint
        element.style.height = height + 'px';
        element.style.removeProperty('padding-top');
        element.style.removeProperty('padding-bottom');
        window.setTimeout(() => {
            element.style.removeProperty('height');
            element.style.removeProperty('overflow');
            element.style.removeProperty('transition-duration');
            element.style.removeProperty('transition-property');
        }, duration);
    }
    
    // Helper function to slide up an element
    function slideUp(element, duration) {
        let height = element.scrollHeight;
        let paddingTop = window.getComputedStyle(element).paddingTop;
        let paddingBottom = window.getComputedStyle(element).paddingBottom;
    
        element.style.overflow = 'hidden';
        element.style.height = height + 'px';
        element.style.paddingTop = paddingTop;
        element.style.paddingBottom = paddingBottom;
        element.style.transitionProperty = `height, margin, padding`;
        element.style.transitionDuration = duration + 'ms';
        element.offsetHeight; // force repaint
        element.style.height = 0;
        element.style.paddingTop = 0;
        element.style.paddingBottom = 0;
        window.setTimeout(() => {
            element.style.display = 'none';
            element.style.removeProperty('height');
            element.style.removeProperty('padding-top');
            element.style.removeProperty('padding-bottom');
            element.style.removeProperty('overflow');
            element.style.removeProperty('transition-duration');
            element.style.removeProperty('transition-property');
        }, duration);
    }
  }

  // XO Gallery - Show the alt text as image title
  if(document.getElementsByClassName('xo-gallery').length > 0) {
    theme.waitForElement('.xo-gallery .xo-hovertype-img .imagebox', 3000).then(function() {
      document.querySelectorAll('.xo-gallery .xo-hovertype-img .imagebox').forEach(function(box) {
          var img = box.querySelector('img');
          if (img && img.getAttribute('alt')) {
              var div = document.createElement('div');
              div.className = 'imagebox-title';
              div.textContent = img.getAttribute('alt');
              box.parentNode.insertBefore(div, box.nextSibling);
          }
      });
    });
  }

  // Fast Bundle Addon

  const fastBundleHandlers = {};
  const fastBundleBuilder = document.getElementsByClassName('rbrBundleBuilder');
  const fastBundlePage = document.getElementsByClassName('rbrProductBundlePosition');
  const fastBundleFBT = document.getElementsByClassName('rbrFBTPosition');

  // Format the product titles with subtitles
  fastBundleHandlers.formatProductTitle = () => {
    theme.waitForElement('[class^="mamProductItemCard_productTitle_"] a, [class^="style_productTitle_"] a', 3000).then(function() {
      // Wait for all the product titles to finish loading
      theme.waitForElementDisappear('.rbr-3dot-wrapper', 3000).then(function() {
        let links = document.querySelectorAll('[class^="mamProductItemCard_productTitle_"] a, [class^="style_productTitle_"] a');
        links.forEach(function(link) {
          setTimeout(() => {
            theme.displayProductSubtitle(link);
          }, 900);
        });
      });
    });
  };

  // Trigger add to cart event based on theme settings
  fastBundleHandlers.addToCart = () => {
    document.addEventListener('fastbundle:bundleadded', (response) => {
      
      // Close popup
      document.querySelector('[class^="modal_container_"] [class^="modal_close_icon_wrapper_"]')?.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
      
      if (theme.settings.afterAddToCart === 'page') {
        // redirect
        setTimeout(() => {
          window.location.href = theme.routes.cart;
        }, 300);
  
      } else if (theme.settings.afterAddToCart === 'drawer' || theme.settings.afterAddToCart === 'notification') {
        // refresh areas
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
        
        if (theme.settings.afterAddToCart === 'drawer') {
          // open the drawer cart
          setTimeout(() => {
            document.querySelector('.js-cart-drawer').open();
          }, 300);
        } 
        else if (theme.settings.afterAddToCart === 'notification') {
          // create notification
          const notification = document.getElementById('AddedNotification').content.firstElementChild.cloneNode(true);
          notification.dataset.productTitle = response.detail.bundle_name;
          let notificationContainer = document.querySelector('.pageheader--sticky');
          if (!js) {
            notificationContainer = document.querySelector('body');
          }
          notificationContainer.appendChild(notification);
        }

        // Clear all added items (need to reverse order to remove the last added item first)
        let addedItems = [...document.querySelectorAll('.rbrBundleBuilder [class^="variant_icon_"]')].reverse();
        addedItems.forEach((item) => {
          item.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
        });
                
      }
    });
  };

  // Force subscription for fast bundle
  fastBundleHandlers.forceSubscription = () => {
    const subscriptionID = fastBundleBuilder[0].dataset.subscribeOnly;

    const subscriptionObserver = new MutationObserver(()=>{
      const subscriptionSelector = document.querySelector('select[class^="select_container_"]');
      if(subscriptionSelector) {
          // Select the subscription option with the given ID
          subscriptionSelector.value = subscriptionID;
          subscriptionSelector.dispatchEvent(new Event('change', { bubbles: true }));

          // Remove the first option 'First Time Purchase'
          // subscriptionSelector.remove(0);
      }
    });

    theme.waitForElement('[class^="cartInfo_card_"]', 3000).then(function() {
      const cartInfo = document.querySelector('[class^="cartInfo_card_body_actions_"], [class^="cartInfo_card_mobile_actions_"]');
      subscriptionObserver.observe(cartInfo, { childList: true, subtree: true });
    });
 
  };

  fastBundleHandlers.fixPosMobileCartInfo = () => {

    const mobileCartInfoObserver = new MutationObserver(() => {

        const cartInfoMobile = document.querySelector('[class^="cartInfo_card_mobile_"]');
        const cartActionsElement = document.querySelector('[class^="cartInfo_card_mobile_actions_"]');

        // Disconnect observer to avoid triggering an infinite loop
        mobileCartInfoObserver.disconnect();

        // Fix the position of the cart info on mobile - Fast Bundle bug
        setTimeout(() => {
            const offsetHeight = cartActionsElement.getBoundingClientRect().height - 1;
            cartInfoMobile.style.bottom = `${offsetHeight}px`;

            // Reconnect the observer after the update
            mobileCartInfoObserver.observe(cartInfoMobile, { attributes: true, attributeFilter: ['style'] });
        }, 300);
    });

    theme.waitForElement('[class^="cartInfo_card_mobile_"]', 3000).then(() => {
        const cartInfoMobile = document.querySelector('[class^="cartInfo_card_mobile_"]');
        // Start observing for attribute changes
        mobileCartInfoObserver.observe(cartInfoMobile, { attributes: true, attributeFilter: ['style'] });
    });
  };

  fastBundleHandlers.expandAccordionFirstPanel = () => {
    theme.waitForElement('[class^="mamProductItemCard_productTitle_"] a', 3000).then(function() {
      // Wait for all the product titles to finish loading
      theme.waitForElementDisappear('.rbr-3dot-wrapper', 3000).then(function() {
        setTimeout(() => {
          const firstPanel = document.querySelector('[class^="Accordion_head_"]');
          if (firstPanel) {
            firstPanel.click();
            firstPanel.dataset.expandedByDefault = 'true';
          }
        }, 900);
      });
    });
  }

  fastBundleHandlers.expandAccordionPanelOnClick = () => {
    theme.waitForElement('[class^="mamProductItemCard_productTitle_"] a', 3000).then(function() {
      // Wait for all the product titles to finish loading
      theme.waitForElementDisappear('.rbr-3dot-wrapper', 3000).then(function() {
        theme.waitForElement('[class^="dashedPlus_icon_"]', 3000).then(function() {
          setTimeout(() => {
            // Clicking the dashed plus icon should also expand the corresponding accordion panel
            const accordionContainers = document.querySelectorAll('[class^="accordions_accordion_"] [class^="Accordion_panel_"], [class="cartInfo_card_"] [class^="cartInfo_card_body_"], [class^="cartInfo_card_added_items_"] + div');
            const collectionContainers = document.querySelectorAll('[class^="collectionsCard_collection_container_"] [class^="Accordion_head_"], [class^="mainSection_accordionContainer_"] [class^="Accordion_head_"]');

            accordionContainers.forEach((container, index) => {
              const observer = new MutationObserver(() => {
                const dashedPlusIcon = container.querySelector('[class^="dashedPlus_icon_"]');
                if (dashedPlusIcon && !dashedPlusIcon.dataset.listenerAttached) {
                  dashedPlusIcon.addEventListener('click', () => {
                    const mobileArrowDown = document.querySelector('[class*="cartInfo_card_mobile_arrow_down_"]');
                    if (mobileArrowDown) {
                      mobileArrowDown.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
                    }
                    
                    if (collectionContainers[index]) {
                      if (collectionContainers[index].dataset.expandedByDefault === 'true') {
                        delete collectionContainers[index].dataset.expandedByDefault;
                      } else {
                        collectionContainers[index].click();
                      }
                    }

                    // Scroll to rbrBundleBuilder only on mobile
                    if (!theme.mediaMatches.md) {
                      const rbrBundleBuilder = document.querySelector('.rbrBundleBuilder');
                      if (rbrBundleBuilder) {
                        const elementPosition = rbrBundleBuilder.getBoundingClientRect().top + window.pageYOffset;
                        const offsetPosition = elementPosition - theme.stickyHeaderHeight();
                        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                      }
                    }
                      
                  });
                  dashedPlusIcon.dataset.listenerAttached = 'true';
                }
              });

              observer.observe(container, { childList: true, subtree: true });
              
              // Trigger initial check
              const initialIcon = container.querySelector('[class^="dashedPlus_icon_"]');
              if (initialIcon && !initialIcon.dataset.listenerAttached) {
                initialIcon.addEventListener('click', () => {
                  const mobileArrowDown = document.querySelector('[class*="cartInfo_card_mobile_arrow_down_"]');
                  if (mobileArrowDown) {
                    mobileArrowDown.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
                  }
                  
                  if (collectionContainers[index]) {
                    if (collectionContainers[index].dataset.expandedByDefault === 'true') {
                      delete collectionContainers[index].dataset.expandedByDefault;
                    } else {
                      collectionContainers[index].click();
                    }
                  }

                  // Scroll to rbrBundleBuilder only on mobile
                  if (!theme.mediaMatches.md) {
                    const rbrBundleBuilder = document.querySelector('.rbrBundleBuilder');
                    if (rbrBundleBuilder) {
                      const elementPosition = rbrBundleBuilder.getBoundingClientRect().top + window.pageYOffset;
                      const offsetPosition = elementPosition - theme.stickyHeaderHeight();
                      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                    }
                  }

                });
                initialIcon.dataset.listenerAttached = 'true';
              }

            });

            // Click on Add Bundle button will open the first incomplete accordion panel
            const addToCartBtn = document.querySelector('#rbr-modal-body [class^="AddToCartBtn_button_"], .rbrBundleBuilder [class^="AddToCartBtn_button_"], .rbrBundleBuilder .rbr-addBundleBtn-container');
            if (addToCartBtn) {

              // Fix: Disable the add to cart button for the new Bundle Builder
              if (addToCartBtn.classList.contains('rbr-disable') && !addToCartBtn.hasAttribute('disabled')) {
                addToCartBtn.setAttribute('disabled', ''); 
              }

              addToCartBtn.addEventListener('click', (evt) => {
                if (addToCartBtn.hasAttribute('disabled')) {
                  evt.preventDefault();
                  evt.stopPropagation();
                  
                  const firstIncompleteAccordion = document.querySelector('[class^="collectionsCard_collection_container_"] [class^="Accordion_head_"]:not(:has([class^="style_fillComplete_"])), [class^="mainSection_accordionContainer_"] [class^="Accordion_head_"]:not(:has([class^="style_fillComplete_"]))');
                  if (firstIncompleteAccordion) {
                    if (firstIncompleteAccordion.dataset.expandedByDefault === 'true') {
                      delete firstIncompleteAccordion.dataset.expandedByDefault;
                    } else {
                      firstIncompleteAccordion.click();
                    }
                  }

                  // Scroll to rbrBundleBuilder only on mobile
                  if (!theme.mediaMatches.md) {
                    const rbrBundleBuilder = document.querySelector('.rbrBundleBuilder');
                    if (rbrBundleBuilder) {
                      const elementPosition = rbrBundleBuilder.getBoundingClientRect().top + window.pageYOffset;
                      const offsetPosition = elementPosition - theme.stickyHeaderHeight();
                      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                    }
                  }

                }
              });
            }
          }, 1000);
        });
      });
    });

  }

  if (fastBundleBuilder.length > 0) {
    if(fastBundleBuilder[0].hasAttribute('data-subscribe-only')) fastBundleHandlers.forceSubscription();
    // fastBundleHandlers.fixPosMobileCartInfo();
    fastBundleHandlers.formatProductTitle();
    if(fastBundleBuilder[0].hasAttribute('data-expand-accordion')) fastBundleHandlers.expandAccordionFirstPanel();
    fastBundleHandlers.expandAccordionPanelOnClick();
    fastBundleHandlers.addToCart();
  }

  if (fastBundlePage.length > 0) {
    theme.waitForElement('.rbr-page-container [class^="button_container_"]', 3000).then(function() {
      document.querySelector('.rbr-page-container [class^="button_container_"]').addEventListener('click', () => {
        theme.waitForElement('#rbr-modal-body', 3000).then(function() {

          // Fix the bug for accordion panels
          // document.querySelectorAll('#rbr-modal-body [class^="cartInfo_card_"] [class^="Accordion_panel_"]').forEach((menu) => {
          //   menu.style.maxHeight = null;
          // });
          
          // fastBundleHandlers.fixPosMobileCartInfo();
          fastBundleHandlers.formatProductTitle();
          fastBundleHandlers.expandAccordionPanelOnClick();
          fastBundleHandlers.addToCart();
        }); 
      });
    }).catch(e => {
      // console.log('Element(s) with ".rbr-addBundleBtn-container" identifiers not found!');
    });
  }

  if (fastBundleFBT.length > 0) {
    fastBundleHandlers.addToCart();
    theme.waitForElement(
        '[class^="modernFBTBundle_container_"] [class^="horizontalBundleItems_item_wrapper_"]:last-child [class^="horizontalBundleItems_title_"], ' +
        '[class^="modernFBTBundle_container_"] [class^="verticalBundleItems_item_wrapper_"]:last-child [class^="verticalBundleItems_title_"]',
        10000
      )
      .then(function () {
        const container = document.getElementById("rbr-container-element-fbt");

        setTimeout(() => {
          // Update the product title
          container
            .querySelectorAll('[class^="horizontalBundleItems_title_"]')
            .forEach(function (link) {
              theme.displayProductSubtitle(link);
            });

          // Select the first variant
          theme
            .waitForElement(
              '#rbr-container-element-fbt [class^="singleSelectBox_select_"] option:last-child',
              3000
            )
            .then(function () {
              container
                .querySelectorAll('[class^="singleSelectBox_select_"')
                .forEach(function (select) {
                  select.selectedIndex = 1;
                  select.dispatchEvent(new Event("change", { bubbles: true }));
                  // remove the first option 'Select variant'
                  select.remove(0);

                  // Move the selector container just before the pricing label
                  const pricingLabel = select.closest('[class^="horizontalBundleItems_item_wrapper_"], [class^="verticalBundleItems_item_wrapper_"]').querySelector('[class^="horizontalBundleItems_priceTagWrapper_"], [class^="verticalBundleItems_priceTagWrapper_"]');
                  const selectorContainer = select.closest('[class^="variantSelector_select_container_"]');
                  pricingLabel.parentNode.insertBefore(selectorContainer, pricingLabel); 
                });
            })          
            .catch((e) => {
              console.log(
                'Element(s) with "#rbr-container-element-fbt [class^="singleSelectBox_select_"] option:last-child" identifiers not found!'
              );
            });

          // Fetch and update the routine steps
          let fetchPromises = [];

          container
            .querySelectorAll(
              '[class^="horizontalBundleItems_item_wrapper_"], [class^="verticalBundleItems_selector_"]'
            )
            .forEach(function (product) {
              const url = product.querySelector("a").href;

              fetchPromises.push(
                fetchProductInfo(url).then((info) => {
                  if (info) {
                    product.dataset.step = info.step;
                    product.dataset.routine = info.routine;
                  }
                  return product;
                })
              );
            });

          Promise.all(fetchPromises)
            .then((products) => {
              // Collect all step and routine values
              let stepRoutines = products.map((product) => ({
                step: product.dataset.step,
                routine: product.dataset.routine,
              }));

              // Sort and replace steps in stepRoutine
              let sortedStepRoutines = sortAndReplaceSteps(stepRoutines);

              // Update each product's dataset with the new sorted stepRoutine
              products.forEach((product, index) => {
                product.dataset.step = sortedStepRoutines[index].step;
                product.dataset.routine = sortedStepRoutines[index].routine;
                // Reorder product on desktop view
                if (
                  product.className.startsWith(
                    "horizontalBundleItems_item_wrapper_"
                  )
                ) {
                  product.style.setProperty(
                    "--order",
                    sortedStepRoutines[index].order * 2
                  );
                }
                // Reorder product on mobile view
                else {
                  product.parentNode.style.setProperty(
                    "--order",
                    sortedStepRoutines[index].order
                  );
                }

                // Set loaded attribute
                container.setAttribute("fetched", "");
              });

              // Reorder the plus icons
              container
                .querySelectorAll('[class^="horizontalBundleItems_plus_"')
                .forEach(function (plus, index) {
                  plus.style.setProperty("--order", index * 2 + 1);
                });

              // Align product block heights in this row
              const blocksInThisRow = container.querySelectorAll('[class^="horizontalBundleItems_item_wrapper_"]');

              // Align the heights of the variant text blocks
              let tallest = 0;
              blocksInThisRow.forEach((el) => {
                el.querySelector('[class^="horizontalBundleItems_variant_text_"], [class^="variantSelector_select_container_"]').style.removeProperty('--fbt-block-height');
              });
              blocksInThisRow.forEach((el) => {
                const elInnerHeight = el.querySelector('[class^="horizontalBundleItems_variant_text_"], [class^="variantSelector_select_container_"]').clientHeight;
                if (elInnerHeight > tallest) tallest = elInnerHeight;
              });
              blocksInThisRow.forEach((el) => {
                el.querySelector('[class^="horizontalBundleItems_variant_text_"], [class^="variantSelector_select_container_"]').style.setProperty('--fbt-block-height', `${tallest}px`);
              });
              

              // Align the heights of the product title text blocks
              tallest = 0;
              blocksInThisRow.forEach((el) => {
                el.querySelector('[class^="horizontalBundleItems_title_"]').style.removeProperty('--fbt-block-height');
              });
              blocksInThisRow.forEach((el) => {
                const elInnerHeight = parseFloat(window.getComputedStyle(el.querySelector('[class^="horizontalBundleItems_title_"]')).height);
                if (elInnerHeight > tallest) tallest = elInnerHeight;
              });
              blocksInThisRow.forEach((el) => {
                el.querySelector('[class^="horizontalBundleItems_title_"]').style.setProperty('--fbt-block-height', `${tallest}px`);
              });

              // Align the heights of the treatment step text blocks
              tallest = 0;
              blocksInThisRow.forEach((el) => {
                el.style.removeProperty('--fbt-block-height');
              });
              blocksInThisRow.forEach((el) => {
                const elInnerHeight = parseFloat(window.getComputedStyle(el, ':before').height);
                if (elInnerHeight > tallest) tallest = elInnerHeight;
              });
              blocksInThisRow.forEach((el) => {
                el.style.setProperty('--fbt-block-height', `${tallest}px`);
              });
            })
            .catch((e) => {
              console.error("Failed to fetch metafields: ", e);
            })
            .finally(() => {
              // Unhide the widget
              container.setAttribute("loaded", "");
            });
          }, 300);
      })
      .catch((e) => {
        console.log(
          'Element(s) with "[class^="modernFBTBundle_container_"]" identifiers not found!'
        );
      });
    
  }

  async function fetchProductInfo(url) {
    try {
        const response = await fetch(url + '?view=metafields');
        if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.statusText}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('There was a problem with the fetch operation:', error);
        return null;
    }
  }

  function sortAndReplaceSteps(arr) {
    // Create an array of objects with original indices and values
    let indexedArray = arr.map((value, index) => ({ value, index }));
  
    // Sort the array based on the step and routine values
    indexedArray.sort((a, b) => {
      const valueA = `${a.value.step} - ${a.value.routine}`;
      const valueB = `${b.value.step} - ${b.value.routine}`;
      return valueA.localeCompare(valueB);
    });
  
    // Initialize the current step counter
    let currentStep = 1;

    // Regex to match the first number in the string
    const numberRegex = /\d+/;
  
    // Map over the sorted array to replace step numbers
    let resultArray = indexedArray.map((item, index, array) => {
      let newValue = {
        // Replace the first number with the current step number
        step: item.value.step.replace(numberRegex, currentStep),
        routine: item.value.routine,
        order: index
      };
  
      // Increment the current step if the next element is different
      let currentStepString = item.value.step;
      let nextStepString = (index + 1 < array.length) ? array[index + 1].value.step : null;
  
      if (currentStepString !== nextStepString) {
        currentStep++;
      }
  
      return { ...item, value: newValue };
    });
  
    // Sort back to the original order based on the original indices
    resultArray.sort((a, b) => a.index - b.index);
  
    // Extract the values
    let sortedSteps = resultArray.map(item => item.value);
  
    return sortedSteps;
  }

  // If it is a Fast Bundle product page and they hide the add to cart button to show their own bundle builder button
  if (window.FastBundleConf?.pid && !window.FastBundleConf?.bap_override_fetch) {
    if (window.FastBundleConf.bap_ids.includes(+window.FastBundleConf.pid)) {
      fastBundleHandlers.addToCart();
      fastBundleHandlers.formatProductTitle();
      
      theme.waitForElement('.js-product-form >  .quantity-submit-row  .rbr-bap-new-footer[disabled]').then(() => {
        setTimeout(() => {
          const bapFooter = document.querySelector('.js-product-form > .quantity-submit-row .rbr-bap-new-footer');
          
          if (bapFooter) {
            bapFooter.addEventListener('click', (evt) => {
              if (bapFooter.hasAttribute('disabled')) {
                evt.preventDefault();
                evt.stopPropagation();
                
                // Open the first incomplete accordion panel
                const firstAccordionHead = document.querySelector('#rbr-container-element-true [class^="Accordion_head_"]:not(:has([class^="style_fillComplete_"]))');
                if (firstAccordionHead) {
                  firstAccordionHead.click();
                }
              }
            });
          }
        }, 1000);
      });
    }
  }

    
});
